import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Plus,
  Filter,
  Download,
  RefreshCw,
  FileText,
  MoreHorizontal,
  Edit,
  Trash2,
  Loader2,
} from "lucide-react";
import { getAccountBills, deleteAccountBill } from "../core/_requests";
import { useAuth } from '../../auth';
import { ACCOUNTS_PERMS } from '../core/permissions';
import { AccountsActionDropdown, DropdownPosition, getDropdownPosition } from './AccountsActionDropdown';

interface Bill {
  id: number;
  vendorId: number | null;
  vendorName: string | null;
  billNumber: string;
  referenceNumber: string | null;
  orderNumber: string | null;
  billDate: string | null;
  dueDate: string | null;
  paymentTerms: string | null;
  subject: string | null;
  subTotal: number | null;
  total: number | null;
  balanceDue: number | null;
  status: string | null;
}

export default function AccountsPurchaseBills() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("All");
  const [loading, setLoading] = useState(true);
  const [bills, setBills] = useState<Bill[]>([]);
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const [dropdownPos, setDropdownPos] = useState<DropdownPosition | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const filters = ["All", "Draft", "Open", "Overdue", "Paid", "Partially Paid"];

  useEffect(() => {
    setLoading(true);
    getAccountBills()
      .then((data) => setBills(data || []))
      .catch((err) => console.error("Failed to load bills:", err))
      .finally(() => setLoading(false));
  }, [refreshTrigger]);

  useEffect(() => {
    const closeDropdowns = () => {
      setActiveDropdown(null);
      setDropdownPos(null);
    };
    document.addEventListener('click', closeDropdowns);
    return () => document.removeEventListener('click', closeDropdowns);
  }, []);

  const handleRefresh = () => setRefreshTrigger((prev) => prev + 1);

  const handleDelete = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    if (!window.confirm("Are you sure you want to delete this bill?")) return;
    try {
      await deleteAccountBill(id);
      setBills((prev) => prev.filter((b) => b.id !== id));
    } catch (err: any) {
      console.error("Failed to delete bill:", err);
      alert("Error deleting bill");
    }
  };

  // Dynamic stats
  const safeNumber = (val: any): number => {
    let num = Number(val);
    if (isNaN(num)) {
      const clean = typeof val === 'string' ? val.replace(/[^0-9.-]/g, '') : '';
      num = Number(clean);
      if (isNaN(num)) num = 0;
    }
    return num;
  };

  const getDerivedStatuses = (b: Bill): string[] => {
    const statuses: string[] = [];
    const baseStatus = (b.status || "Draft").toLowerCase();
    
    // Map database statuses to UI statuses
    let mappedStatus = baseStatus === 'paid' ? 'Paid' : (baseStatus === 'draft' ? 'Draft' : (baseStatus === 'cancelled' ? 'Cancelled' : b.status || "Draft"));
    
    if (['paid', 'draft', 'cancelled'].includes(baseStatus)) {
      statuses.push(mappedStatus);
    } else {
      statuses.push(mappedStatus);
    }
    
    if (b.dueDate && !['paid', 'draft', 'cancelled'].includes(baseStatus)) {
      const due = new Date(b.dueDate);
      const today = new Date();
      due.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      if (due < today) {
        if (!statuses.includes('Overdue')) {
          statuses.push('Overdue');
        }
      }
    }
    
    return statuses.length > 0 ? statuses : ['Draft'];
  };

  const formatCurrency = (val: any) => {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(safeNumber(val));
  };

  const totalBills = bills.length;
  const paidBillsValue = bills.reduce((acc, b) => {
    const t = safeNumber(b.total);
    const bal = b.balanceDue !== undefined && b.balanceDue !== null ? safeNumber(b.balanceDue) : t;
    return acc + Math.max(0, t - bal);
  }, 0);
  const outstandingBillsValue = bills
    .filter((b) => getDerivedStatuses(b).some(s => s === "Open" || s === "Partially Paid" || s === "Overdue"))
    .reduce((acc, b) => acc + safeNumber(b.balanceDue), 0);
  const overdueBillsValue = bills
    .filter((b) => getDerivedStatuses(b).includes("Overdue"))
    .reduce((acc, b) => acc + safeNumber(b.balanceDue), 0);

  const stats = [
    { label: "Total Bills", value: totalBills.toString(), color: "blue" },
    {
      label: "Paid",
      value: formatCurrency(paidBillsValue),
      color: "emerald",
    },
    {
      label: "Outstanding",
      value: formatCurrency(outstandingBillsValue),
      color: "amber",
    },
    {
      label: "Overdue",
      value: formatCurrency(overdueBillsValue),
      color: "red",
    },
  ];

  // Filter & search
  const filteredBills = bills.filter((b) => {
    if (activeFilter !== "All") {
      const derived = getDerivedStatuses(b);
      if (!derived.includes(activeFilter)) return false;
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const vName = b.vendorName || (b as any).vendor_name;
      return (
        b.billNumber?.toLowerCase().includes(q) ||
        vName?.toLowerCase().includes(q) ||
        b.referenceNumber?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const getStatusBadgeClass = (status: string | null) => {
    switch (status) {
      case "Draft":
        return "bg-slate-100 text-slate-700 border-slate-200";
      case "Open":
        return "bg-blue-50 text-blue-700 border-blue-200/50";
      case "Paid":
        return "bg-emerald-50 text-emerald-700 border-emerald-200/50";
      case "Partially Paid":
        return "bg-teal-50 text-teal-700 border-teal-200/50";
      case "Overdue":
        return "bg-rose-50 text-rose-700 border-rose-200/50";
      default:
        return "bg-slate-100 text-slate-600 border-slate-200";
    }
  };

  const handleDownloadCSV = () => {
    if (filteredBills.length === 0) {
      alert("No bill data to export.");
      return;
    }
    const formatCurrency = (val: any) => {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(safeNumber(val));
    };
    const headers = ["Sr. No.", "Bill #", "Vendor", "Date", "Due Date", "Amount", "Balance", "Status"];
    const rows = filteredBills.map((b, index) => [
      index + 1,
      b.billNumber || '-',
      b.vendorName || '-',
      b.billDate ? new Date(b.billDate).toLocaleDateString() : "-",
      b.dueDate ? new Date(b.dueDate).toLocaleDateString() : "-",
      formatCurrency(b.total || 0),
      formatCurrency(b.balanceDue || 0),
      getDerivedStatuses(b).join(', ')
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `bills_export_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const b = activeDropdown !== null
    ? filteredBills.find((b) => b.id === activeDropdown)
    : null;

  return (
    <>
      <div className="flex h-screen w-full overflow-hidden bg-slate-50">
        
        <div id="sidebar-overlay" className="fixed inset-0 bg-black/50 z-40 hidden md:hidden"></div>
        <main className="flex-1 flex flex-col h-full overflow-hidden relative">
          
          <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6 space-y-5" id="main-scroll">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                  Accounts / Purchase
                </div>
                <h1 className="text-xl font-bold text-slate-900 leading-tight">Bills</h1>
                <p className="text-xs text-slate-500">Track and manage vendor bills and payments</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 text-[11px] text-slate-500">
                  <span>Refresh data</span>
                  <button onClick={handleRefresh} className="p-1 rounded hover:bg-slate-100 transition-colors">
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                  </button>
                </div>
                {hasPermission(ACCOUNTS_PERMS.BILL_CREATE) && (
                  <button
                    onClick={() => navigate("/accounts/purchase/bills/new")}
                    className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold shadow-sm hover:shadow-md transition-all flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    New Bill
                  </button>
                )}
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              {stats.map((stat, i) => (
                <div key={i} className="card-hover bg-white rounded-2xl p-5 border border-slate-200 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <div className={`w-9 h-9 rounded-xl bg-${stat.color}-50 flex items-center justify-center`}>
                      <FileText className={`w-4.5 h-4.5 text-${stat.color}-600`} />
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-slate-900">{stat.value}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Table */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
                  {filters.map((f) => (
                    <button
                      key={f}
                      onClick={() => setActiveFilter(f)}
                      className={`px-3 py-1.5 text-[11px] font-bold rounded-lg whitespace-nowrap transition-all ${
                        activeFilter === f ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"
                      }`}
                    >
                      {f}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search bills..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 pr-4 py-2 w-56 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                    />
                  </div>
                  {/* Filter button kept for future use.
                  <button className="p-2 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors">
                    <Filter className="w-4 h-4 text-slate-500" />
                  </button>
                  */}
                  <button onClick={handleDownloadCSV} className="p-2 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors">
                    <Download className="w-4 h-4 text-slate-500" />
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left min-w-175">
                  <thead>
                    <tr className="text-[10px] text-slate-400 uppercase tracking-wider bg-slate-50/50">
                      <th className="px-4 py-3 font-medium text-center text-slate-400">Sr. No.</th>
                      <th className="px-4 py-3 font-medium">Bill #</th>
                      <th className="px-4 py-3 font-medium">Vendor</th>
                      <th className="px-4 py-3 font-medium">Date</th>
                      <th className="px-4 py-3 font-medium">Due Date</th>
                      <th className="px-4 py-3 font-medium text-right">Amount</th>
                      <th className="px-4 py-3 font-medium text-right">Balance</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-sm">
                    {loading ? (
                      <tr>
                        <td colSpan={9} className="p-16 text-center">
                          <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-4" />
                          <p className="text-slate-500 font-medium">Loading bills...</p>
                        </td>
                      </tr>
                    ) : filteredBills.length === 0 ? (
                      <tr>
                        <td colSpan={9}>
                          <div className="p-16 flex flex-col items-center justify-center">
                            <div className="w-20 h-20 bg-linear-to-br from-blue-50 to-blue-100 text-blue-500 rounded-2xl flex items-center justify-center mb-5">
                              <FileText className="w-10 h-10" strokeWidth={1.5} />
                            </div>
                            <h3 className="text-lg font-bold text-slate-900 mb-2">
                              {searchQuery ? "No matching bills" : "No bills found"}
                            </h3>
                            <p className="text-slate-500 text-sm text-center max-w-sm mb-6">
                              Record vendor bills to track your payables and manage cash flow.
                            </p>
                            {!searchQuery && (
                              hasPermission(ACCOUNTS_PERMS.BILL_CREATE) && (
                                <button
                                  onClick={() => navigate("/accounts/purchase/bills/new")}
                                  className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold shadow-sm hover:shadow-md transition-all flex items-center gap-2"
                                >
                                  <Plus className="w-4 h-4" />
                                  Create Bill
                                </button>
                              )
                            )}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredBills.map((b, index) => (
                        <tr
                          key={b.id}
                          className="hover:bg-slate-50 transition-colors group cursor-pointer"
                          onClick={() => hasPermission(ACCOUNTS_PERMS.BILL_UPDATE) && navigate(`/accounts/purchase/bills/${b.id}`)}
                        >
                          <td className="px-4 py-3 text-slate-500 font-medium text-center" onClick={(e) => e.stopPropagation()}>
                            {index + 1}
                          </td>
                          <td className="px-4 py-3 font-semibold text-blue-600 hover:underline">
                            {b.billNumber}
                          </td>
                          <td className="px-4 py-3 font-medium text-slate-700">{b.vendorName || (b as any).vendor_name || "-"}</td>
                          <td className="px-4 py-3 text-slate-500">
                            {b.billDate ? new Date(b.billDate).toLocaleDateString() : "-"}
                          </td>
                          <td className="px-4 py-3 text-slate-500">
                            {b.dueDate ? new Date(b.dueDate).toLocaleDateString() : "-"}
                          </td>
                          <td className="px-4 py-3 font-semibold text-slate-900 text-right">
                            {formatCurrency(b.total)}
                          </td>
                          <td className="px-4 py-3 font-semibold text-slate-900 text-right">
                            {formatCurrency(b.balanceDue)}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {getDerivedStatuses(b).map(s => (
                                <span key={s} className={`inline-block px-2.5 py-1 text-[10px] font-bold rounded-full border ${getStatusBadgeClass(s)}`}>
                                  {s}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right relative" onClick={(e) => e.stopPropagation()}>
                            <button
                              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all focus:opacity-100"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (activeDropdown === b.id) {
                                  setActiveDropdown(null);
                                  setDropdownPos(null);
                                } else {
                                  setDropdownPos(getDropdownPosition(e.currentTarget as HTMLElement));
                                  setActiveDropdown(b.id);
                                }
                              }}
                            >
                              <MoreHorizontal className="w-4 h-4" />
                            </button>
                            
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            
          </div>
        </main>
      </div>

      {b && (
        <AccountsActionDropdown open={true} position={dropdownPos}>
                                {hasPermission(ACCOUNTS_PERMS.BILL_UPDATE) && (
                                  <button
                                    className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setActiveDropdown(null);
                                      setDropdownPos(null);
                                      navigate(`/accounts/purchase/bills/${b.id}`);
                                    }}
                                  >
                                    <Edit className="w-4 h-4 text-slate-400" /> Edit Bill
                                  </button>
                                )}
                                {hasPermission(ACCOUNTS_PERMS.BILL_DELETE) && (
                                  <>
                                    <div className="h-px bg-slate-100 my-1"></div>
                                    <button
                                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                      onClick={(e) => {
                                        setActiveDropdown(null);
                                      setDropdownPos(null);
                                        handleDelete(e, b.id);
                                      }}
                                    >
                                      <Trash2 className="w-4 h-4 text-red-400" /> Delete Bill
                                    </button>
                                  </>
                                )}
        </AccountsActionDropdown>
      )}

    </>
  );
}
